import React, { useEffect, useState } from "react";
import { PhoneCall, MessageSquare, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * "Ring me now" button — guest enters their phone, Twilio calls them back,
 * then bridges to the Janté concierge desk. Self-disables if Twilio isn't
 * configured (the backend returns `configured: false` from /twilio/status).
 *
 * Compact prop = pill-style for inline use in the trip-ref banner. Default =
 * a fuller bar with copy.
 */
export default function RingMeNowButton({ tripRef = null, compact = false }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [twilioReady, setTwilioReady] = useState(null); // null = unknown / loading

  useEffect(() => {
    api
      .get("/twilio/status")
      .then((r) => setTwilioReady(!!r.data?.configured))
      .catch(() => setTwilioReady(false));
  }, []);

  // Hide entirely until we know whether Twilio is configured. Showing then
  // hiding the button would feel buggy.
  if (twilioReady === null || twilioReady === false) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setBusy(true);
    try {
      // If the guest opted into SMS at the same time, register that consent first.
      if (smsOptIn) {
        try {
          await api.post("/twilio/sms/opt-in", {
            phone,
            consent: true,
            trip_ref: tripRef,
          });
        } catch (err) {
          // Don't block the call on SMS opt-in failure — log it
          console.warn("SMS opt-in failed", err);
        }
      }
      const r = await api.post("/twilio/ring-me", { phone, trip_ref: tripRef });
      if (r.data?.ok) {
        toast.success("Calling you now — pick up to be connected to your concierge.");
        setOpen(false);
        setPhone("");
      } else {
        toast.error("Could not place the call. Please dial +1 445 45Jante (52683) directly.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not place the call.";
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          compact
            ? "inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 bg-[var(--brand-red)] text-[var(--ivory)] hover:opacity-90 transition-opacity"
            : "inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] px-5 py-3 bg-[var(--brand-red)] text-[var(--ivory)] hover:opacity-90 transition-opacity"
        }
        data-testid="ring-me-now-btn"
      >
        <PhoneCall size={compact ? 11 : 13} strokeWidth={1.75} />
        Ring me now
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-[80] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          data-testid="ring-me-modal"
        >
          <div className="bg-[var(--ivory)] max-w-md w-full p-6 sm:p-7 relative shadow-2xl">
            <button
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 text-[var(--ink-2)] hover:text-[var(--brand-red)]"
              aria-label="Close"
            >
              <X size={18} strokeWidth={1.5} />
            </button>

            <div className="overline text-[var(--brand-red)] flex items-center gap-2">
              <PhoneCall size={12} strokeWidth={1.5} /> Ring me now
            </div>
            <h2 className="font-display text-3xl text-[var(--emerald)] mt-3 leading-tight">
              We'll call you in a moment.
            </h2>
            <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed">
              Enter the number you'd like us to ring. Our concierge will be on
              the line when you pick up.{tripRef ? ` Your trip reference ${tripRef} will be passed along.` : ""}
            </p>

            <form onSubmit={submit} className="mt-5 space-y-4">
              <div>
                <label className="overline text-[var(--brand-red)] block mb-1.5">
                  Mobile number
                </label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                  className="lux-input w-full"
                  data-testid="ring-me-phone"
                  autoFocus
                />
              </div>

              <label className="flex items-start gap-3 text-xs text-[var(--ink-2)] leading-snug cursor-pointer">
                <input
                  type="checkbox"
                  checked={smsOptIn}
                  onChange={(e) => setSmsOptIn(e.target.checked)}
                  className="mt-1 flex-shrink-0"
                  data-testid="ring-me-sms-optin"
                />
                <span className="flex items-center gap-1.5">
                  <MessageSquare size={11} strokeWidth={1.5} className="text-[var(--gold)]" />
                  Also text me my trip updates · Msg & data rates may apply · Reply STOP to opt out.
                </span>
              </label>

              <p className="text-[10px] uppercase tracking-[0.16em] text-[var(--muted)]">
                Calls may be recorded for quality &amp; itinerary-confirmation purposes ·
                <a href="/legal#calls" target="_blank" rel="noreferrer" className="text-[var(--brand-red)] hover:text-[var(--emerald)] ml-1">
                  policy
                </a>
              </p>

              <div className="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={busy}
                  className="text-xs uppercase tracking-[0.18em] text-[var(--ink-2)] hover:text-[var(--emerald)] px-4 py-2.5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy || !phone.trim()}
                  className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="ring-me-submit"
                >
                  {busy ? "Ringing…" : "Call me"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
